// src/Feuerloescher.js
import React, { useState, useEffect, useRef } from "react";
import { exportFeuerloescherToExcel, importFeuerloescherFromExcel } from "../services/excelFeuerloescherService";
import {
  autoReconnectDirectory,
  hasSelectedDirectory,
  selectDirectory,
  saveFileToDirectory,
  listFilesInDirectory
} from "../utils/fileSystem";
import "./Feuerloescher.css";

const initialFields = {
  nummer: "",
  art: "",
  menge: "",
  ablauf: "",
  blombe: false,
  einsatz: false,
  standort: "",
  bemerkung: ""
};

const ARTEN = ["Pulver", "CO₂", "Schaum", "Wasser", "Fettbrand", "Sonstiges"];

const BEISPIEL_DATEN = [
  { nummer: "1001", art: "Pulver", menge: "6", ablauf: "2025-12-31", blombe: false, einsatz: false, standort: "Deck 1, Raum 12", bemerkung: "" },
  { nummer: "1002", art: "CO₂", menge: "5", ablauf: "2024-09-15", blombe: false, einsatz: false, standort: "Halle A", bemerkung: "" },
  { nummer: "1003", art: "Schaum", menge: "9", ablauf: "2023-11-01", blombe: true, einsatz: true, standort: "Vorschiff", bemerkung: "Einsatz 2024" }
];

// Konstanten für Datei-Operationen
const CATEGORY = "feuerloescher";
const FILE_PREFIX = "Liste Feuerlöscher FDB";
const FILE_EXTENSION = ".xlsx";
const MAIN_FILE_NAME = `${FILE_PREFIX}${FILE_EXTENSION}`;
const TRASH_FILE_PREFIX = "Papierkorb Feuerlöscher FDB";
const TRASH_FILE_NAME = `${TRASH_FILE_PREFIX}${FILE_EXTENSION}`;

const Feuerloescher = () => {
  const [list, setList] = useState([]);
  const [papierkorb, setPapierkorb] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [fields, setFields] = useState(initialFields);
  const [showModal, setShowModal] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isDirectorySelected, setIsDirectorySelected] = useState(false);
  const nummerInputRef = useRef(null);

  // Beim Start der Komponente:
  // 1. Automatisch mit dem letzten Verzeichnis verbinden
  // 2. Daten aus der neuesten Datei laden
  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);
      setMessage({ text: "Initialisiere Komponente...", type: "info" });

      try {
        const hasDirectory = await hasSelectedDirectory(CATEGORY);
        setIsDirectorySelected(hasDirectory);

        if (hasDirectory) {
          const reconnected = await autoReconnectDirectory(CATEGORY);

          if (reconnected) {
            await Promise.all([
              loadNewestDataFile(),
              loadTrashData() // Lade auch die Papierkorb-Daten
            ]);
          } else {
            setMessage({ 
              text: "Verbindung zum Speicherort nicht möglich. Bitte wählen Sie erneut einen Speicherort in den Einstellungen aus.", 
              type: "warning" 
            });
          }
        } else {
          setMessage({ 
            text: "Kein Speicherort konfiguriert. Bitte wählen Sie einen Speicherort in den Einstellungen aus.", 
            type: "info" 
          });
          // Beispiel-Daten nur laden, wenn kein Speicherort konfiguriert ist
          if (list.length === 0) setList(BEISPIEL_DATEN);
        }
      } catch (error) {
        console.error("Fehler bei der Initialisierung:", error);
        setMessage({ 
          text: `Fehler beim Initialisieren: ${error.message}`, 
          type: "error" 
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, []);

  // Fokus auf Nummer-Feld im Modal
  useEffect(() => {
    if (showModal && nummerInputRef.current) {
      nummerInputRef.current.focus();
    }
  }, [showModal]);

  // Hilfsfunktionen für Status
  const getStatus = (ablauf, einsatz) => {
    if (einsatz) return "einsatz";
    if (!ablauf) return "ok";
    const d = new Date(ablauf);
    const now = new Date();
    if (d < now) return "abgelaufen";
    if ((d - now) / (1000 * 60 * 60 * 24) < 60) return "bald";
    return "ok";
  };

  // Funktion zum Laden der Hauptdaten-Datei
  const loadNewestDataFile = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "Lade Daten aus Speicherort...", type: "info" });

      const files = await listFilesInDirectory(CATEGORY, FILE_PREFIX, FILE_EXTENSION);
      const mainFile = files.find(file => file.name === MAIN_FILE_NAME);

      if (!mainFile) {
        setMessage({ 
          text: "Keine Datendatei im Speicherort gefunden. Sie können neue Einträge erstellen.", 
          type: "info" 
        });
        setIsLoading(false);
        return;
      }

      const arrayBuffer = await mainFile.file.arrayBuffer();
      const data = await importFeuerloescherFromExcel(new Blob([arrayBuffer]));
      setList(data);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
      setMessage({ 
        text: `Fehler beim Laden der Daten: ${error.message}`, 
        type: "error" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion zum Laden der Papierkorb-Daten
  const loadTrashData = async () => {
    try {
      const files = await listFilesInDirectory(CATEGORY, TRASH_FILE_PREFIX, FILE_EXTENSION);
      
      if (files.length === 0) {
        setPapierkorb([]);
        return;
      }

      const trashFile = files.find(file => file.name === TRASH_FILE_NAME);
      if (!trashFile) {
        setPapierkorb([]);
        return;
      }

      const arrayBuffer = await trashFile.file.arrayBuffer();
      const data = await importFeuerloescherFromExcel(new Blob([arrayBuffer]));
      setPapierkorb(data);
    } catch (error) {
      console.error("Fehler beim Laden der Papierkorb-Daten:", error);
      setMessage({ 
        text: `Fehler beim Laden der Papierkorb-Daten: ${error.message}`, 
        type: "error" 
      });
    }
  };

  // Hinzufügen/Bearbeiten
  const handleSave = async () => {
    if (!fields.nummer || !fields.art || !fields.menge || !fields.ablauf || !fields.standort) {
      alert("Bitte alle Pflichtfelder ausfüllen!");
      return;
    }
    // Doppelte Nummer verhindern
    const nummerExists = list.some((e, i) => e.nummer === fields.nummer && i !== editing);
    if (nummerExists) {
      alert("Diese Nummer ist bereits vergeben!");
      return;
    }
    let newList;
    if (editing !== null) {
      newList = list.map((e, i) => (i === editing ? { ...fields } : e));
    } else {
      newList = [...list, { ...fields }];
    }
    setList(newList);
    setFields(initialFields);
    setEditing(null);
    setShowModal(false);

    // Speichern im Verzeichnis
    if (isDirectorySelected) {
      await saveData(newList);
    }
  };

  // Löschen
  const handleDelete = async (idx) => {
    if (!window.confirm("Wirklich löschen?")) return;
    const entry = list[idx];
    const newPapierkorb = [...papierkorb, entry];
    setPapierkorb(newPapierkorb);
    const newList = list.filter((_, i) => i !== idx);
    setList(newList);

    // Speichern im Verzeichnis
    if (isDirectorySelected) {
      await Promise.all([
        saveData(newList),
        saveTrashData(newPapierkorb)
      ]);
    }
  };

  // Wiederherstellen
  const handleRestore = async (idx) => {
    if (!window.confirm("Wiederherstellen?")) return;
    const entry = papierkorb[idx];
    const newList = [...list, entry];
    setList(newList);
    const newPapierkorb = papierkorb.filter((_, i) => i !== idx);
    setPapierkorb(newPapierkorb);

    // Speichern im Verzeichnis
    if (isDirectorySelected) {
      await Promise.all([
        saveData(newList),
        saveTrashData(newPapierkorb)
      ]);
    }
  };

  // Speichern der Hauptdaten
  const saveData = async (dataToSave) => {
    try {
      if (!isDirectorySelected) {
        const dirSelected = await selectDirectory(CATEGORY);
        if (!dirSelected) {
          setMessage({ 
            text: "Kein Speicherort ausgewählt. Die Daten wurden nicht gespeichert.", 
            type: "error" 
          });
          return;
        }
        setIsDirectorySelected(true);
      }

      const excelBuffer = await exportFeuerloescherToExcel(dataToSave);
      await saveFileToDirectory(CATEGORY, MAIN_FILE_NAME, excelBuffer);
      
      setMessage({ 
        text: `Daten erfolgreich als Excel-Datei gespeichert: ${MAIN_FILE_NAME}`, 
        type: "success" 
      });
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      let errorMessage = "Fehler beim Speichern";
      if (error.name === 'NotAllowedError') {
        errorMessage = "Keine Berechtigung zum Speichern im ausgewählten Verzeichnis. Bitte wählen Sie erneut einen Speicherort aus.";
      } else if (error.name === 'SecurityError') {
        errorMessage = "Sicherheitsfehler beim Zugriff auf das Dateisystem. Bitte verwenden Sie einen modernen Browser wie Chrome oder Edge.";
      } else {
        errorMessage = `Fehler beim Speichern: ${error.message}`;
      }
      setMessage({ text: errorMessage, type: "error" });
    }
  };

  // Speichern der Papierkorb-Daten
  const saveTrashData = async (dataToSave) => {
    try {
      if (!isDirectorySelected) {
        const dirSelected = await selectDirectory(CATEGORY);
        if (!dirSelected) {
          setMessage({ 
            text: "Kein Speicherort ausgewählt. Die Daten wurden nicht gespeichert.", 
            type: "error" 
          });
          return;
        }
        setIsDirectorySelected(true);
      }

      const excelBuffer = await exportFeuerloescherToExcel(dataToSave, true);
      await saveFileToDirectory(CATEGORY, TRASH_FILE_NAME, excelBuffer);
    } catch (error) {
      console.error("Fehler beim Speichern der Papierkorb-Daten:", error);
      setMessage({ 
        text: `Fehler beim Speichern der Papierkorb-Daten: ${error.message}`, 
        type: "error" 
      });
    }
  };

  // Excel Export/Import
  const handleExport = async () => {
    if (!isDirectorySelected) {
      const dirSelected = await selectDirectory(CATEGORY);
      if (!dirSelected) {
        setMessage({ 
          text: "Bitte wählen Sie zuerst einen Speicherort aus.", 
          type: "warning" 
        });
        return;
      }
      setIsDirectorySelected(true);
    }
    await saveData(list);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const data = await importFeuerloescherFromExcel(file);
      setList(data);
      if (isDirectorySelected) {
        await saveData(data);
      }
    }
  };

  const handleTrashExport = async () => {
    if (!isDirectorySelected) {
      const dirSelected = await selectDirectory(CATEGORY);
      if (!dirSelected) {
        setMessage({ 
          text: "Bitte wählen Sie zuerst einen Speicherort aus.", 
          type: "warning" 
        });
        return;
      }
      setIsDirectorySelected(true);
    }
    await saveTrashData(papierkorb);
  };

  // Filter
  const filtered = list.filter(e => {
    const textMatch = Object.values(e).join(" ").toLowerCase().includes(filter.toLowerCase());
    const status = getStatus(e.ablauf, e.einsatz);
    const statusMatch = !statusFilter || status === statusFilter;
    return textMatch && statusMatch;
  });

  return (
    <div className="feuerloescher-container">
      <h2>Feuerlöscher-Verwaltung</h2>
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      <div className="fl-actions">
        <button onClick={() => { setShowModal(true); setEditing(null); setFields(initialFields); }}>Neuer Feuerlöscher</button>
        <button onClick={handleExport}>Exportieren</button>
        <label className="import-label">
          Importieren
          <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={handleImport} />
        </label>
        <button onClick={() => setShowTrash(!showTrash)}>{showTrash ? "Zurück" : "Papierkorb"}</button>
        {showTrash && <button onClick={handleTrashExport}>Papierkorb-Export</button>}
        <input placeholder="Suche/Filter..." value={filter} onChange={e => setFilter(e.target.value)} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 15}}>
          <option value="">Alle Status</option>
          <option value="ok">OK</option>
          <option value="bald">Bald ablaufend</option>
          <option value="abgelaufen">Abgelaufen</option>
          <option value="einsatz">Einsatz</option>
        </select>
      </div>
      {showModal && (
        <div className="fl-modal">
          <div className="fl-modal-card">
            <h3>{editing !== null ? "Bearbeiten" : "Neuer Feuerlöscher"}</h3>
            <div className="fl-form-grid">
              <label>Nummer*<input ref={nummerInputRef} value={fields.nummer} onChange={e => setFields(f => ({ ...f, nummer: e.target.value }))} /></label>
              <label>Art*<select value={fields.art} onChange={e => setFields(f => ({ ...f, art: e.target.value }))}>{ARTEN.map(a => <option key={a}>{a}</option>)}</select></label>
              <label>Liter/kg*<input type="number" value={fields.menge} onChange={e => setFields(f => ({ ...f, menge: e.target.value }))} /></label>
              <label>Ablaufdatum*<input type="date" value={fields.ablauf} onChange={e => setFields(f => ({ ...f, ablauf: e.target.value }))} /></label>
              <label>Blombe gezogen<input type="checkbox" checked={fields.blombe} onChange={e => setFields(f => ({ ...f, blombe: e.target.checked }))} /></label>
              <label>Zum Einsatz gekommen<input type="checkbox" checked={fields.einsatz} onChange={e => setFields(f => ({ ...f, einsatz: e.target.checked }))} /></label>
              <label>Standort*<input value={fields.standort} onChange={e => setFields(f => ({ ...f, standort: e.target.value }))} /></label>
              <label>Bemerkungen<input value={fields.bemerkung} onChange={e => setFields(f => ({ ...f, bemerkung: e.target.value }))} /></label>
            </div>
            <div className="fl-modal-actions">
              <button onClick={handleSave}>Speichern</button>
              <button onClick={() => setShowModal(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
      <div className="fl-table-wrapper">
        <table className="fl-table">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Art</th>
              <th>Liter/kg</th>
              <th>Ablaufdatum</th>
              <th>Blombe</th>
              <th>Einsatz</th>
              <th>Standort</th>
              <th>Bemerkungen</th>
              {!showTrash && <th>Aktionen</th>}
            </tr>
          </thead>
          <tbody>
            {(showTrash ? papierkorb : filtered).map((e, idx) => (
              <tr key={e.nummer + idx} className={getStatus(e.ablauf, e.einsatz)}>
                <td>{e.nummer}</td>
                <td>{e.art}</td>
                <td>{e.menge}</td>
                <td>{e.ablauf}</td>
                <td>{e.blombe ? "Ja" : "Nein"}</td>
                <td>{e.einsatz ? "Ja" : "Nein"}</td>
                <td>{e.standort}</td>
                <td>{e.bemerkung}</td>
                {!showTrash && (
                  <td>
                    <button onClick={() => { setEditing(idx); setFields(e); setShowModal(true); }}>Bearbeiten</button>
                    <button onClick={() => handleDelete(idx)}>Löschen</button>
                  </td>
                )}
                {showTrash && (
                  <td>
                    <button onClick={() => handleRestore(idx)}>Wiederherstellen</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Feuerloescher;
